import { selectFiles } from '@app/components/FileUpload/modules/fileUpload'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { AssociatedDevelopmentClient } from '@app/models/AssociatedDevelopmentClient'
import { AssociatedDevelopmentSite } from '@app/models/AssociatedDevelopmentSite'
import { Attachment } from '@app/models/Attachment'
import { IDevelopmentSite } from '@app/models/DevelopmentSite'
import {
  DueDiligenceItem,
  IDueDiligenceItem,
} from '@app/models/DueDiligenceItem'
import { DevelopmentPipelineType } from '@app/models/enums'
import { FileUploadAttachment } from '@app/models/FileUploadAttachment'
import { Reminder } from '@app/models/Reminder'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export const ATTACHMENTS_IDENTIFIER = 'due-diligence-item-attachment-editor'

// -------------------------
// STATE
// -------------------------
export interface IDueDiligenceItemEditState {
  readonly error?: string
  readonly isOpen: boolean
  readonly isSaving: boolean
  readonly item: IDueDiligenceItem
}

const DueDiligenceItemEditStateRecord = Record({
  error: undefined,
  isOpen: false,
  isSaving: false,
  item: new DueDiligenceItem(),
})

export class DueDiligenceItemEditState extends DueDiligenceItemEditStateRecord
  implements IDueDiligenceItemEditState {
  public static fromJS(initialState?: Partial<IDueDiligenceItemEditState>) {
    if (!initialState) {
      return new DueDiligenceItemEditState()
    }

    const { item, ...rest } = initialState

    return new DueDiligenceItemEditState({
      item: DueDiligenceItem.fromJS(item),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isOpen: boolean
  public readonly isSaving: boolean
  public readonly item: DueDiligenceItem

  constructor(props?: Partial<DueDiligenceItemEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadAddDueDiligenceItem = 'DUE_DILIGENCE_ITEM_EDIT::LOAD_ADD_DUE_DILIGENCE_ITEM',
  LoadEditDueDiligenceItem = 'DUE_DILIGENCE_ITEM_EDIT::LOAD_EDIT_DUE_DILIGENCE_ITEM',
  ResetState = 'DUE_DILIGENCE_ITEM_EDIT::RESET_STATE',
  SaveDueDiligenceItem = 'DUE_DILIGENCE_ITEM_EDIT::SAVE',
  SaveDueDiligenceItemFailure = 'DUE_DILIGENCE_ITEM_EDIT::SAVE_FAILURE',
  SaveDueDiligenceItemRequest = 'DUE_DILIGENCE_ITEM_EDIT::SAVE_REQUEST',
  SaveDueDiligenceItemSuccess = 'DUE_DILIGENCE_ITEM_EDIT::SAVE_SUCCESS',
  SetAssignedTo = 'DUE_DILIGENCE_ITEM_EDIT::SET_ASSIGNED_TO',
  SetCompletedDate = 'DUE_DILIGENCE_ITEM_EDIT::SET_COMPLETED_DATE',
  SetContacts = 'DUE_DILIGENCE_ITEM_EDIT::SET_CONTACTS',
  SetDueDate = 'DUE_DILIGENCE_ITEM_EDIT::SET_DUE_DATE',
  SetEarnestMoneyAmount = 'DUE_DILIGENCE_ITEM_EDIT::SET_EARNEST_MONEY_AMOUNT',
  SetEarnestMoneyDepositDate = 'DUE_DILIGENCE_ITEM_EDIT::SET_EARNEST_MONEY_DEPOSIT_DATE',
  SetEarnestMoneyHoldingLocation = 'DUE_DILIGENCE_ITEM_EDIT::SET_EARNEST_MONEY_HOLDING_LOCATION',
  SetEarnestMoneyWithdrawlDate = 'DUE_DILIGENCE_ITEM_EDIT::SET_EARNEST_MONEY_WITHDRAWL_DATE',
  SetIsComplete = 'DUE_DILIGENCE_ITEM_EDIT::SET_IS_COMPLETE',
  SetIsCriticalDate = 'DUE_DILIGENCE_ITEM_EDIT::SET_IS_CRITICAL_DATE',
  SetIsPublic = 'DUE_DILIGENCE_ITEM_EDIT::SET_IS_PUBLIC',
  SetReminders = 'DUE_DILIGENCE_ITEM_EDIT::SET_REMINDERS',
  SetResponsibility = 'DUE_DILIGENCE_ITEM_EDIT::SET_RESPONSIBILITY',
  SetTitle = 'DUE_DILIGENCE_ITEM_EDIT::SET_TITLE',
  SetType = 'DUE_DILIGENCE_ITEM_EDIT::SET_TYPE',
}

interface ILoadAddDueDiligenceItemAction {
  pipelineType: DevelopmentPipelineType
  site: IDevelopmentSite
  type: ActionTypes.LoadAddDueDiligenceItem
}

interface ILoadEditDueDiligenceItemAction {
  item: DueDiligenceItem
  type: ActionTypes.LoadEditDueDiligenceItem
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveDueDiligenceItemAction {
  item: IDueDiligenceItem
  type: ActionTypes.SaveDueDiligenceItem
}

interface ISaveDueDiligenceItemFailureAction {
  error: string
  type: ActionTypes.SaveDueDiligenceItemFailure
}

interface ISaveDueDiligenceItemRequestAction {
  type: ActionTypes.SaveDueDiligenceItemRequest
}

export interface ISaveDueDiligenceItemSuccessAction {
  item: DueDiligenceItem
  type: ActionTypes.SaveDueDiligenceItemSuccess
}

interface ISetAssignedToAction {
  assignedTo: List<AssociatedContact>
  type: ActionTypes.SetAssignedTo
}

interface ISetCompletedDateAction {
  completedDate?: number
  type: ActionTypes.SetCompletedDate
}

interface ISetContactsAction {
  contacts: List<AssociatedContact>
  type: ActionTypes.SetContacts
}

interface ISetDueDateAction {
  dueDate?: number
  type: ActionTypes.SetDueDate
}

interface ISetEarnestMoneyAmountAction {
  amount: number
  type: ActionTypes.SetEarnestMoneyAmount
}

interface ISetEarnestMoneyDepositDateAction {
  depositDate?: number
  type: ActionTypes.SetEarnestMoneyDepositDate
}

interface ISetEarnestMoneyHoldingLocationAction {
  holdingLocation: string
  type: ActionTypes.SetEarnestMoneyHoldingLocation
}

interface ISetEarnestMoneyWithdrawlDateAction {
  type: ActionTypes.SetEarnestMoneyWithdrawlDate
  withdrawlDate?: number
}

interface ISetIsCompleteAction {
  isComplete: boolean
  type: ActionTypes.SetIsComplete
}

interface ISetIsCriticalDateAction {
  isCriticalDate: boolean
  type: ActionTypes.SetIsCriticalDate
}

interface ISetIsPublicAction {
  isPublic: boolean
  type: ActionTypes.SetIsPublic
}

interface ISetRemindersAction {
  reminders: List<Reminder>
  type: ActionTypes.SetReminders
}

interface ISetResponsibilityAction {
  responsibility: string
  type: ActionTypes.SetResponsibility
}

interface ISetTitleAction {
  title: string
  type: ActionTypes.SetTitle
}

interface ISetTypeAction {
  itemType: string
  type: ActionTypes.SetType
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadAddDueDiligenceItemAction
  | ILoadEditDueDiligenceItemAction
  | IResetStateAction
  | ISaveDueDiligenceItemFailureAction
  | ISaveDueDiligenceItemRequestAction
  | ISaveDueDiligenceItemSuccessAction
  | ISetAssignedToAction
  | ISetCompletedDateAction
  | ISetContactsAction
  | ISetDueDateAction
  | ISetEarnestMoneyAmountAction
  | ISetEarnestMoneyDepositDateAction
  | ISetEarnestMoneyHoldingLocationAction
  | ISetEarnestMoneyWithdrawlDateAction
  | ISetIsCompleteAction
  | ISetIsCriticalDateAction
  | ISetIsPublicAction
  | ISetRemindersAction
  | ISetResponsibilityAction
  | ISetTitleAction
  | ISetTypeAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadAddDueDiligenceItem(
  pipelineType: DevelopmentPipelineType,
  site: IDevelopmentSite,
): ILoadAddDueDiligenceItemAction {
  return {
    pipelineType,
    site,
    type: ActionTypes.LoadAddDueDiligenceItem,
  }
}

function loadEditDueDiligenceItem(
  item: DueDiligenceItem,
): ILoadEditDueDiligenceItemAction {
  return {
    item,
    type: ActionTypes.LoadEditDueDiligenceItem,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveDueDiligenceItem(
  item: IDueDiligenceItem,
): ISaveDueDiligenceItemAction {
  return {
    item,
    type: ActionTypes.SaveDueDiligenceItem,
  }
}

function saveDueDiligenceItemFailure(
  error: string,
): ISaveDueDiligenceItemFailureAction {
  return {
    error,
    type: ActionTypes.SaveDueDiligenceItemFailure,
  }
}

function saveDueDiligenceItemRequest(): ISaveDueDiligenceItemRequestAction {
  return { type: ActionTypes.SaveDueDiligenceItemRequest }
}

function saveDueDiligenceItemSuccess(
  item: DueDiligenceItem,
): ISaveDueDiligenceItemSuccessAction {
  return {
    item,
    type: ActionTypes.SaveDueDiligenceItemSuccess,
  }
}

function setAssignedTo(
  assignedTo: List<AssociatedContact>,
): ISetAssignedToAction {
  return {
    assignedTo,
    type: ActionTypes.SetAssignedTo,
  }
}

function setCompletedDate(completedDate?: number): ISetCompletedDateAction {
  return {
    completedDate,
    type: ActionTypes.SetCompletedDate,
  }
}

function setContacts(contacts: List<AssociatedContact>): ISetContactsAction {
  return {
    contacts,
    type: ActionTypes.SetContacts,
  }
}

function setDueDate(dueDate?: number): ISetDueDateAction {
  return {
    dueDate,
    type: ActionTypes.SetDueDate,
  }
}

function setEarnestMoneyAmount(amount: number): ISetEarnestMoneyAmountAction {
  return {
    amount,
    type: ActionTypes.SetEarnestMoneyAmount,
  }
}

function setEarnestMoneyDepositDate(
  depositDate?: number,
): ISetEarnestMoneyDepositDateAction {
  return {
    depositDate,
    type: ActionTypes.SetEarnestMoneyDepositDate,
  }
}

function setEarnestMoneyHoldingLocation(
  holdingLocation: string,
): ISetEarnestMoneyHoldingLocationAction {
  return {
    holdingLocation,
    type: ActionTypes.SetEarnestMoneyHoldingLocation,
  }
}

function setEarnestMoneyWithdrawlDate(
  withdrawlDate?: number,
): ISetEarnestMoneyWithdrawlDateAction {
  return {
    type: ActionTypes.SetEarnestMoneyWithdrawlDate,
    withdrawlDate,
  }
}

function setIsComplete(isComplete: boolean): ISetIsCompleteAction {
  return {
    isComplete,
    type: ActionTypes.SetIsComplete,
  }
}

function setIsCriticalDate(isCriticalDate: boolean): ISetIsCriticalDateAction {
  return {
    isCriticalDate,
    type: ActionTypes.SetIsCriticalDate,
  }
}

function setIsPublic(isPublic: boolean): ISetIsPublicAction {
  return {
    isPublic,
    type: ActionTypes.SetIsPublic,
  }
}

function setReminders(reminders: List<Reminder>): ISetRemindersAction {
  return {
    reminders,
    type: ActionTypes.SetReminders,
  }
}

function setResponsibility(responsibility: string): ISetResponsibilityAction {
  return {
    responsibility,
    type: ActionTypes.SetResponsibility,
  }
}

function setTitle(title: string): ISetTitleAction {
  return {
    title,
    type: ActionTypes.SetTitle,
  }
}

function setType(itemType: string): ISetTypeAction {
  return {
    itemType,
    type: ActionTypes.SetType,
  }
}

export const actionCreators = {
  loadAddDueDiligenceItem,
  loadEditDueDiligenceItem,
  resetState,
  saveDueDiligenceItem,
  setAssignedTo,
  setCompletedDate,
  setContacts,
  setDueDate,
  setEarnestMoneyAmount,
  setEarnestMoneyDepositDate,
  setEarnestMoneyHoldingLocation,
  setEarnestMoneyWithdrawlDate,
  setIsComplete,
  setIsCriticalDate,
  setIsPublic,
  setReminders,
  setResponsibility,
  setTitle,
  setType,
}

export const externalActionCreators = {
  loadAddDueDiligenceItem,
  loadEditDueDiligenceItem,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new DueDiligenceItemEditState()

export const reducer: Reducer<DueDiligenceItemEditState> = (
  state: DueDiligenceItemEditState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadAddDueDiligenceItem: {
      const newItem = new DueDiligenceItem({
        client: action.site.client as AssociatedDevelopmentClient,
        pipelineType: action.pipelineType,
        site: new AssociatedDevelopmentSite({
          siteId: action.site.id,
          title: action.site.title,
        }),
      })

      return state
        .set('isOpen', true)
        .set('item', newItem) as DueDiligenceItemEditState
    }

    case ActionTypes.LoadEditDueDiligenceItem:
      return state
        .set('isOpen', true)
        .set('item', action.item) as DueDiligenceItemEditState

    case ActionTypes.ResetState:
      return new DueDiligenceItemEditState()

    case ActionTypes.SaveDueDiligenceItemFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as DueDiligenceItemEditState

    case ActionTypes.SaveDueDiligenceItemRequest:
      return state.set('isSaving', true) as DueDiligenceItemEditState

    case ActionTypes.SaveDueDiligenceItemSuccess:
      return state.set('isSaving', false) as DueDiligenceItemEditState

    case ActionTypes.SetAssignedTo:
      return state.setIn(
        ['item', 'assignedTo'],
        action.assignedTo,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetCompletedDate:
      return state.setIn(
        ['item', 'completedDate'],
        action.completedDate,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetContacts:
      return state.setIn(
        ['item', 'contacts'],
        action.contacts,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetDueDate:
      return state.setIn(
        ['item', 'dueDate'],
        action.dueDate,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetEarnestMoneyAmount:
      return state.setIn(
        ['item', 'earnestMoney', 'amount'],
        action.amount,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetEarnestMoneyDepositDate:
      return state.setIn(
        ['item', 'earnestMoney', 'depositDate'],
        action.depositDate,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetEarnestMoneyHoldingLocation:
      return state.setIn(
        ['item', 'earnestMoney', 'holdingLocation'],
        action.holdingLocation,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetEarnestMoneyWithdrawlDate:
      return state.setIn(
        ['item', 'earnestMoney', 'withdrawlDate'],
        action.withdrawlDate,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetIsComplete:
      return state.setIn(
        ['item', 'isComplete'],
        action.isComplete,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetIsCriticalDate:
      return state.setIn(
        ['item', 'isCriticalDate'],
        action.isCriticalDate,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetIsPublic:
      return state.setIn(
        ['item', 'isPublic'],
        action.isPublic,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetReminders:
      return state.setIn(
        ['item', 'reminders'],
        action.reminders,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetResponsibility:
      return state.setIn(
        ['item', 'responsibility'],
        action.responsibility,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetTitle:
      return state.setIn(
        ['item', 'title'],
        action.title,
      ) as DueDiligenceItemEditState

    case ActionTypes.SetType:
      return state.setIn(
        ['item', 'type'],
        action.itemType,
      ) as DueDiligenceItemEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* saveDueDiligenceItemSaga(action: ISaveDueDiligenceItemAction) {
  try {
    yield put(saveDueDiligenceItemRequest())

    const attachments: Map<string, FileUploadAttachment> = yield select(
      selectFiles(ATTACHMENTS_IDENTIFIER),
    )

    const attachmentsList = attachments
      ? attachments.toArray().reduce((list, fileUpload) => {
          if (fileUpload === undefined || fileUpload.error) {
            return list
          }

          return list.push(fileUpload.attachment)
        }, List<Attachment>())
      : List<Attachment>()

    const item = (action.item as DueDiligenceItem)
      .set('attachments', attachmentsList)
      .toJS()

    const reqOpts: IRequestOptions = {
      body: JSON.stringify(item),
      method: item.id ? RequestMethod.PUT : RequestMethod.POST,
    }

    const reqUrl = item.id
      ? `/api/DevelopmentSites/${action.item.site.siteId}/DueDiligence/${
          action.item.id
        }`
      : `/api/DevelopmentSites/${action.item.site.siteId}/DueDiligence`

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      const updatedItem = DueDiligenceItem.fromJS(result.entity)
      yield put(saveDueDiligenceItemSuccess(updatedItem))
      yield put(resetState())
      yield put(
        successToast('Success', `${updatedItem.title} saved successfully!`),
      )
    } else {
      yield put(saveDueDiligenceItemFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveDueDiligenceItemFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchSaveDueDiligenceItem() {
  yield takeEvery(ActionTypes.SaveDueDiligenceItem, saveDueDiligenceItemSaga)
}

export function* dueDiligenceItemEditRootSaga() {
  yield fork(watchSaveDueDiligenceItem)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.dueDiligenceItemEdit

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsOpen = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isOpen,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )

export const selectItem = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.item,
  )
